import {
  LOCALE_ID,
  ErrorHandler,
  NgModule,
  isDevMode,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';

import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { AppComponentModules } from './modules/app-component.module';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { AppUpdateService } from 'src/app/shared/services/app-update.service';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';

import { SidenavComponent } from './components/layout/sidenav/sidenav.component';

import { PopUpComponent } from './components/shared/pop-up/pop-up.component';
import {
  NgHttpCachingConfig,
  NgHttpCachingModule,
  NgHttpCachingStrategy,
} from 'ng-http-caching';
import { CommonModule, registerLocaleData } from '@angular/common';
import { SnAutoCompleteComponent } from './components/shared/sn-auto-complete/sn-auto-complete.component';
import { AutoCompleteComponent } from './components/shared/auto-complete/auto-complete.component';
import { WarningpopUpComponent } from './components/shared/warningpop-up/warningpop-up.component';
import { AppComponent } from './app.component';
import { environment } from 'src/environments/environment';
import { ProducerInquiriesComponent } from './views/producer-inquiries/producer-inquiries.component';
import localeDe from '@angular/common/locales/de';
import { AddButtonComponent } from './components/matchmaking/add-button/add-button.component';
import { TokenInterceptor } from './interceptors/token.interceptor';
import { StaleWriteInterceptor } from './interceptors/stale-write.interceptor';
import { ContractTermBannerComponent } from './components/shared/contract-term-banner/contract-term-banner.component';
import { TicketFormComponent } from './components/admin/ticket-form/ticket-form.component';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { SelectNaceSearchComponent } from './components/awk/select-nace-search/select-nace-search.component';
import { CreateEditDialogComponent } from './components/shared/create-edit-dialog/create-edit-dialog.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';

const ngHttpCachingConfig: NgHttpCachingConfig = {
  cacheStrategy: NgHttpCachingStrategy.DISALLOW_ALL,
};

registerLocaleData(localeDe);

function initializeKeycloak(keycloak: KeycloakService) {
  return () => {
    console.log('=== Keycloak Initialization Starting ===');
    console.log('Keycloak URL:', environment.kcRealmUrl);
    console.log('Realm:', environment.kcRealm);
    console.log('Client ID:', 'wastics_frontend');

    return keycloak
      .init({
        config: {
          url: environment.kcRealmUrl,
          realm: environment.kcRealm,
          clientId: 'wastics_frontend',
        },
        initOptions: {
          onLoad: 'login-required',
          checkLoginIframe: false,
          pkceMethod: 'S256',
        },
        shouldAddToken: () => true,
        bearerExcludedUrls: ['/assets'],
        shouldUpdateToken(request) {
          return !request.headers.get('token-update') === false;
        },
      })
      .then(
        (authenticated) => {
          console.log('=== Keycloak Initialization Successful ===');
          console.log('User authenticated:', authenticated);
          if (authenticated) {
            try {
              const keycloakInstance = keycloak.getKeycloakInstance();
              const tokenParsed = keycloakInstance?.tokenParsed;
              const token = keycloakInstance?.token;

              if (tokenParsed) {
                const username =
                  tokenParsed['preferred_username'] ||
                  tokenParsed['sub'] ||
                  'unknown';
                console.log('User info:', username);
              }

              if (token) {
                console.log('Token:', token.substring(0, 50) + '...');
              }

              if (tokenParsed?.exp) {
                console.log(
                  'Token expires at:',
                  new Date(tokenParsed.exp * 1000)
                );
              }
            } catch (e) {
              console.log(
                'Could not load token details (user profile may still be loading)'
              );
            }
          }
          return authenticated;
        },
        (error) => {
          console.error('=== Keycloak Initialization Failed ===');
          console.error('Error:', error);
          console.error('Error details:', {
            message: error?.message,
            stack: error?.stack,
            response: error?.response,
          });
          throw error;
        }
      );
  };
}

@NgModule({
  declarations: [],
  bootstrap: [AppComponent],
  imports: [
    NgHttpCachingModule.forRoot(ngHttpCachingConfig),
    AppComponentModules,
    KeycloakAngularModule,
    SnAutoCompleteComponent,
    AutoCompleteComponent,
    WarningpopUpComponent,
    ProducerInquiriesComponent,
    SidenavComponent,
    AddButtonComponent,
    CommonModule,
    ContractTermBannerComponent,
    NgxMatSelectSearchModule,
    CreateEditDialogComponent,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    PopUpComponent,
    TicketFormComponent,
    SelectNaceSearchComponent,
  ],
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      const initializerFn = initializeKeycloak(inject(KeycloakService));
      // Update check first: a fresh start reloads into a new version before
      // the Keycloak login starts on the old one.
      return inject(AppUpdateService)
        .checkOnStartup()
        .then(() => initializerFn());
    }),
    { provide: LOCALE_ID, useValue: 'de' },
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: StaleWriteInterceptor,
      multi: true,
    },
    provideCharts(withDefaultRegisterables()),
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
